import "../../TopNavBar.scss";

import clsx from "clsx";
import { FC, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { usePageActions } from "../../../utils/page";
import {
  FeatureStatus,
  useBorderedPresentationColoring,
  usePresentation,
  usePresentationCaption,
  useShouldShowPresentationCaption,
} from "../../../utils/presentation";
import { TopicSidebarPanel } from "../topic/TopicSidebarPanel";

import { DisplayCaption } from "components/DisplayCaption";
import { LoadingScreen } from "components/LoadingScreen";
import { withActiveAv } from "components/av/AvSubsystemLoader";
import { TOPIC_ID_QUERY_PARAM } from "components/constants";
import { PageSecondarySidebar } from "components/materials/page/PageSecondarySidebar";
import { PageSidebar } from "components/materials/page/PageSidebar";
import { AssessmentStudentQueue } from "components/materials/page/assessment/AssessmentStudentQueue";
import { NoPageView } from "components/materials/page/content/NoPageView";
import { PageOverview } from "components/materials/page/content/PageOverview";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { t } from "i18n/i18n";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectCurrentSectionId,
  selectPageViewData,
  selectQueryString,
} from "store/selectors";
import { useTrackEventWithOrgAndCourse } from "utils/amplitude";
import { useAv } from "utils/av";
import { useNavigate, useQueryParamsManager } from "utils/routing";
import { pageUrl } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const LazyScreenshareDisplay = withActiveAv(
  () => import("components/av/ScreenshareDisplay"),
  undefined,
  true,
);

export type PageViewProps = {
  pageId?: string;
};

export const PageView: FC<PageViewProps> = ({ pageId }) => {
  const navigate = useNavigate();
  const { getQueryParam } = useQueryParamsManager();
  const queryString = useAppSelector(selectQueryString);
  const isMobile = !useIsMdOrLarger();
  const isLargeScreen = useIsLgOrLarger();
  const { selectPage } = usePageActions();
  const { canAuthorSection } = useCapabilities();
  const { presentedPageId, isPresenting, isFollowing, featureStatus } = usePresentation();
  const isAssessing = useIsAssessing();
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const currentTopicId = getQueryParam(TOPIC_ID_QUERY_PARAM);
  const { isInVirtualClassOrObserving } = useAv();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const topicOrCategoryChanged = useRef(false);
  const { selectedPage, pages, topicId } = useAppSelector((s) =>
    selectPageViewData(s, page, currentTopicId),
  );
  const shouldSelectForPresenting = !pages.some((p) => p.id === presentedPageId) && isPresenting;

  useTrackEventWithOrgAndCourse("Page viewed", {
    shouldTrack: !!page,
    eventProperties: { pageTitle: page?.title, pageId: page?.id },
  });

  const shouldShowPresentationCaption = useShouldShowPresentationCaption(false);
  const borderColorClass = useBorderedPresentationColoring();
  const { captionText, captionBgColor, linkText, linkUrl } = usePresentationCaption();

  const pageIsPresenting = presentedPageId === page?.id;
  const featuring =
    pageIsPresenting && featureStatus !== FeatureStatus.NONE && (isPresenting || isFollowing);

  // Navigates the user to the default page if no page is selected.
  // The default page could mean the first activity page in the first topic, or the
  // first activity page in the topic specified by the TOPIC_ID_QUERY_PARAM.
  useEffect(() => {
    if ((!pageId || !selectedPage) && pages.length > 0 && !isMobile && currentSectionId) {
      // Use replace so `/section/:sectionId/page` doesn't become a "dead-end" history entry
      // that immediately forwards back to the default page when the user hits Back.
      navigate(pageUrl(currentSectionId, pages[0].id, { additionalParams: queryString }), {
        replace: true,
      });
    }
  }, [pageId, selectedPage, queryString, pages, currentSectionId, navigate, isMobile]);

  useEffect(() => {
    if (!currentSectionId) {
      return;
    }

    storeApi.pages.list({ section_id: currentSectionId });
    if (topicId) {
      storeApi.activities.list({ topic_id: topicId });
    }
  }, [topicId, currentSectionId, navigate]);

  useEffect(() => {
    if (!currentSectionId || topicId || !page?.category || page?.category !== "activity_page") {
      return;
    }

    // If we don't have a topic id but it's still a topic page we need to load the activities.
    storeApi.activities.list({ section_id: currentSectionId });
  }, [page?.category, topicId, currentSectionId]);

  useEffect(() => {
    topicOrCategoryChanged.current = true;
  }, [topicId, selectedPage?.category]);

  useEffect(() => {
    // When we first display the view, change the topic, or delete a page
    // we select a new page automatically but without affecting some
    // secondary behaviors (like following, updating the presentation state, etc.).
    // In order to actually enforce those behaviors we need to directly call
    // selectPage with the selected page. Also note, that you may have two browsers
    // open on different topics, so we only want to use the browser that has focus.
    if (
      isPresenting &&
      selectedPage &&
      (topicOrCategoryChanged.current || shouldSelectForPresenting) &&
      document.hasFocus()
    ) {
      selectPage(selectedPage);
      topicOrCategoryChanged.current = false;
    }
  }, [isPresenting, selectedPage, selectPage, shouldSelectForPresenting]);

  if (!page && pageId) {
    return <LoadingScreen>{t("content_view.loading_page")}</LoadingScreen>;
  }

  return (
    <div
      className="group/themable flex h-full data-[virtual-class]:rounded-lg"
      data-virtual-class={isInVirtualClassOrObserving || undefined}
    >
      {isMobile && !selectedPage?.id ? (
        // TODO: We should consider showing the CoursePageSidebarPanel here as well.
        //  We may want to add the CATEGORY_QUERY_PARAM back to the urls and keep them there
        <div className="content-sidebar-panel rounded-[inherit]">
          <TopicSidebarPanel topicId={topicId} />
        </div>
      ) : (
        <div
          className={twMerge(
            "flex h-full w-full flex-row overflow-hidden rounded-[inherit]",
            isAssessing ? "lg:items-center lg:justify-center" : "items-stretch",
          )}
        >
          {(!isMobile || selectedPage?.id) && (
            <div
              className={clsx(
                "relative z-0 flex h-full shrink-[2] items-center overflow-hidden rounded-[inherit]",
                !isAssessing || !isLargeScreen ? "w-full grow-[2] basis-2/3" : "w-full grow-[0.2]",
                shouldShowPresentationCaption && `border-2 ${borderColorClass}`,
              )}
            >
              {shouldShowPresentationCaption && (
                <div className="absolute inset-0">
                  <DisplayCaption
                    bgColorClass={captionBgColor}
                    caption={captionText}
                    linkText={linkText}
                    linkUrl={linkUrl}
                  />
                </div>
              )}

              {!isMobile && !isAssessing && (
                <div className="relative flex h-full">
                  <PageSidebar pageId={pageId} topicId={topicId} />
                  {featuring && (
                    <div
                      className={clsx("absolute inset-0 z-10 bg-black-tint-95/80 backdrop-blur-sm")}
                    />
                  )}
                </div>
              )}

              {pageId && isAssessing && isLargeScreen && canAuthorSection && (
                <div className="relative flex h-full max-w-[26vw] bg-white py-4 pl-6">
                  <AssessmentStudentQueue pageId={pageId} />
                </div>
              )}

              <ErrorBoundary>
                {selectedPage ? (
                  <PageOverview
                    pageId={selectedPage.id}
                    pages={pages}
                    selectedTopicId={
                      selectedPage.category === "activity_page" ? topicId : undefined
                    }
                  />
                ) : (
                  <NoPageView />
                )}
              </ErrorBoundary>

              <LazyScreenshareDisplay />
            </div>
          )}

          {!isMobile && (
            <PageSecondarySidebar
              className={twMerge(
                "h-full min-w-[392px] max-w-[520px] overflow-y-auto bg-white px-3 py-4 group-data-[virtual-class]/themable:bg-black-tint-15 group-data-[virtual-class]/themable:py-0 group-data-[virtual-class]/themable:pr-0",
                (!isAssessing || !isLargeScreen) && "flex-1 basis-1/3",
              )}
              page={page}
              shouldShowOverlay={featuring && !isInVirtualClassOrObserving}
            />
          )}
        </div>
      )}
    </div>
  );
};
